/**
 * Food collection detail provider.
 *
 * Separates permission checks (AccessGrant) from domain constraints (default/empty). Consumers
 * typically gate UI with `perms.canX && constraints.isX`.
 */
import {
  createContext,
  useContext as useReactContext,
  useMemo,
  type PropsWithChildren,
} from "react";
import { useQuery, useZero } from "@corpuscomplete/zero-data-transformer/react";
import type { Zero, Row, PromiseWithServerResult } from "@rocicorp/zero";
import { mutators, queries, schema } from "@corpuscomplete/core/zero";
import { FoodCollection } from "@corpuscomplete/core/nutrition/food-collection";
import { Recipe } from "@corpuscomplete/core/nutrition/recipe";
import { AuthUI } from "../../../auth";

type CollectionRow = Row<typeof schema.tables.foodCollection>;

export namespace FoodCollectionDetail {
  export type Context = {
    collectionId: string;
    userId: string;
  };

  const DataContext = createContext<Context | null>(null);

  export type DataProviderProps = PropsWithChildren<{
    collectionId: string;
    userId: string;
  }>;

  export function DataProvider({ children, collectionId, userId }: DataProviderProps) {
    return <DataContext value={{ collectionId, userId }}>{children}</DataContext>;
  }

  export function useContext(): Context {
    const context = useReactContext(DataContext);
    if (!context) {
      throw new Error(
        "FoodCollectionDetail.useContext must be used within FoodCollectionDetail.DataProvider",
      );
    }
    return context;
  }

  export type CollectionInfo = CollectionRow & {
    isDefault: boolean;
  };

  export function useCollection(): CollectionInfo | undefined {
    const { collectionId } = useContext();
    const [collections] = useQuery(queries.foodCollection.forRootManagement());

    return useMemo(() => {
      const collection = collections.find((c) => c.id === collectionId);
      if (!collection) return undefined;
      return {
        ...collection,
        isDefault: collection.defaultForUserId !== null,
      };
    }, [collections, collectionId]);
  }

  export function useFoods(): Recipe.ListItem[] {
    const { collectionId } = useContext();
    const [recipes] = useQuery(
      queries.recipe.listItems({
        collectionId: collectionId ?? undefined,
        limit: 100,
        dir: "forward",
        cursor: null,
      }),
    );
    return recipes;
  }

  export function useAvailableCollections(): Array<{
    id: string;
    name: string;
  }> {
    const { collectionId } = useContext();
    const [collections] = useQuery(queries.foodCollection.forContentManagement());

    return useMemo(
      () =>
        collections.filter((c) => c.id !== collectionId).map((c) => ({ id: c.id, name: c.name })),
      [collections, collectionId],
    );
  }

  export type Permissions = {
    canRename: boolean;
    canDelete: boolean;
    canAddContent: boolean;
    canMoveContent: boolean;
  };

  export function usePermissions(): Permissions {
    const { collectionId } = useContext();
    return AuthUI.usePermissions(FoodCollection.Permissions, collectionId);
  }

  export type DomainConstraints = {
    isRenameable: boolean;
    isDeletable: boolean;
  };

  export function useDomainConstraints(): DomainConstraints {
    const collection = useCollection();
    const foods = useFoods();

    return useMemo(() => {
      if (!collection) {
        return {
          isRenameable: false,
          isDeletable: false,
        };
      }

      const { isDefault } = collection;
      const isEmpty = foods.length === 0;

      return {
        isRenameable: !isDefault,
        isDeletable: !isDefault && isEmpty,
      };
    }, [collection, foods.length]);
  }

  export type Mutations = {
    rename: (name: string) => PromiseWithServerResult;
    del: () => PromiseWithServerResult;
    moveFood: (foodId: string, targetCollectionId: string) => PromiseWithServerResult;
  };

  export function useMutations(): Mutations {
    const z = useZero();
    const { collectionId } = useContext();

    return useMemo(
      () => ({
        rename: (name) => z.mutate(mutators.foodCollection.rename({ id: collectionId, name })),
        del: () => z.mutate(mutators.foodCollection.delete({ id: collectionId })),
        moveFood: (foodId, targetCollectionId) =>
          z.mutate(mutators.foodCollection.moveFood({ foodId, targetCollectionId })),
      }),
      [z, collectionId],
    );
  }

  export function useLoadingState(): { isLoading: boolean } {
    const { collectionId } = useContext();
    const [, collectionDetails] = useQuery(queries.foodCollection.forRootManagement());
    const [, recipesDetails] = useQuery(
      queries.recipe.listItems({
        collectionId: collectionId ?? undefined,
        limit: 100,
        dir: "forward",
        cursor: null,
      }),
    );

    return {
      isLoading: collectionDetails.type === "unknown" || recipesDetails.type === "unknown",
    };
  }

  export function preload(zero: Zero, collectionId: string): void {
    void zero.run(queries.foodCollection.forRootManagement());
    void zero.run(queries.foodCollection.forContentManagement());
    void zero.run(
      queries.recipe.listItems({
        collectionId: collectionId ?? undefined,
        limit: 100,
        dir: "forward",
        cursor: null,
      }),
    );
  }
}
